import type { ECharts, EChartsOption } from 'echarts';
import type { ChartExportRequest } from '@shared/components/chart-toolbar/chart-toolbar.component';
import {
  USE_PUBLICATION_CHART_EXPORT,
  buildPublicationChart,
  downloadChartPng,
  downloadChartPngFromOption,
  downloadChartSvg,
  type ChartPublicationCaption,
} from './chart-export.util';
import {
  USE_PUBLICATION_TABLE_EXPORT,
  buildTablePreface,
  downloadTableCsv,
  downloadTableXlsx,
  withCountryColumn,
} from './table-export.util';
import { downloadMetadataPdf, downloadMetadataTxt } from './metadata-export.util';
import type { TableModel } from './table-model';

/**
 * O que uma tela precisa expor para atender ao menu de export. As funções são avaliadas
 * na hora do clique — assim o export leva sempre o estado atual (ano filtrado, tabela
 * transposta, colunas escolhidas).
 */
export interface VisualizationExportSource {
  chart: () => ECharts | null;
  chartOption: () => EChartsOption | null;
  chartSize: () => { width: number; height: number };
  table: () => TableModel | null;
  /** Ficha do indicador como pares campo/valor; ausente quando a tela não tem metadados. */
  metadata?: () => TableModel | null;
  fileName: () => string;
  sheetName?: () => string;
  /** Título impresso no topo do PDF/TXT de metadados. */
  documentTitle?: () => string;
  /**
   * Título, território, fonte e notas que ficam fora do canvas e da grade.
   * Entra na imagem com `USE_PUBLICATION_CHART_EXPORT` e na planilha com
   * `USE_PUBLICATION_TABLE_EXPORT`.
   */
  caption?: () => ChartPublicationCaption | null;
}

export async function runVisualizationExport(
  format: ChartExportRequest,
  source: VisualizationExportSource,
): Promise<boolean> {
  const fileName = source.fileName();

  if (format === 'png' || format === 'svg') {
    return exportChartImage(format, fileName, source);
  }

  if (format === 'metadata-pdf' || format === 'metadata-txt') {
    const model = source.metadata?.() ?? null;
    if (!model) return false;
    const name = `${fileName}-metadados`;
    const title = source.documentTitle?.() ?? '';
    return format === 'metadata-pdf'
      ? downloadMetadataPdf(model, name, title)
      : downloadMetadataTxt(model, name, title);
  }

  const table = source.table();
  if (!table) return false;

  const caption = USE_PUBLICATION_TABLE_EXPORT ? (source.caption?.() ?? null) : null;
  const grid = withCountryColumn(table, caption);
  const preface = buildTablePreface(grid, caption);
  if (format === 'csv') return downloadTableCsv(grid, fileName, preface);
  return downloadTableXlsx(grid, fileName, source.sheetName?.() ?? 'Dados', preface);
}

async function exportChartImage(
  format: 'png' | 'svg',
  fileName: string,
  source: VisualizationExportSource,
): Promise<boolean> {
  const option = source.chartOption();
  const { width, height } = source.chartSize();

  if (USE_PUBLICATION_CHART_EXPORT && option) {
    const layout = buildPublicationChart(option, { width, height }, source.caption?.() ?? {});
    return format === 'png'
      ? downloadChartPngFromOption(layout.option, fileName, layout.width, layout.height)
      : downloadChartSvg(layout.option, fileName, layout.width, layout.height);
  }

  if (format === 'png') {
    const chart = source.chart();
    return chart ? downloadChartPng(chart, fileName) : false;
  }

  if (!option) return false;
  return downloadChartSvg(option, fileName, width, height);
}
